// ═══════════════════════════════════════════════════════════════
// App.js — verticalstack root component (v2)
// Path: src/App.js
//
// S31 LAYER 1 (Apr 27, 2026) — KEYSTONE REWRITE for sandbox-schema
// migration. Per Migration Change Matrix v1.1, Row 10 (REPLACED).
//
// WHAT CHANGED FROM v1
//   - Stripped: RoleRouter switch (PascalCase role → 4 separate apps)
//   - Stripped: AdminApp, OwnerApp, PMApp, TenantApp imports
//   - Stripped: useFCM call from RoleRouter (RoleRouter is deleted; FCM
//     is also stubbed per Row 5, so removal is doubly safe)
//   - Added:    WorkspaceProvider wrapping the routed tree
//   - Added:    WorkspacePicker, Shell imports
//   - New routing logic:
//       not signed in              → Landing
//       signed in, 0 workspaces    → "no workspaces" message
//       signed in, 1 workspace     → Shell directly (auto-selected)
//       signed in, 2+ workspaces, no current selection
//                                  → WorkspacePicker
//       signed in, current selected → Shell
//
// PRESERVED FROM v1
//   - /lease route (LeaseSigningPortal — sub-portal, not part of Layer 1)
//   - /guest route (GuestPortal — sub-portal, not part of Layer 1)
//   - InstallBanner above all routes
//   - Inline-styled fallback messages (kept verbatim including hex colors
//     per branding-deferred memory #30)
//   - Auth-aware root route (sign-in gates the protected tree)
//
// DELETED COMPONENT IMPORTS (orphaned — see Row 11)
//   AdminApp, OwnerApp, PMApp — remain in src/components/ as reference
//   per AS-K. They are no longer imported anywhere. Layer 2+ absorbs
//   their feature content into role-scoped views inside Shell.
//
// LTR_Components — TenantApp was imported from this file; the file
// itself remains in repo (preserves LeaseExecution exports) but the
// TenantApp import is removed.
// ═══════════════════════════════════════════════════════════════

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { WorkspaceProvider } from './hooks/useWorkspace';
import InstallBanner from './components/InstallBanner';
import Login from './components/Login';
import WorkspacePicker from './components/WorkspacePicker';
import Shell from './components/Shell';
import GuestPortal from './components/GuestPortal';
import { LeaseSigningPortal } from './components/LeaseExecution';
import Landing from './Landing';

// ── Inline style for fallback states (preserved from v1) ──────────────────
const fallbackContainer = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  flexDirection: 'column',
  fontFamily: 'Arial, sans-serif',
  backgroundColor: '#F8F9FA',
};

// ── Authenticated routing decision ────────────────────────────────────────
// This component runs only when the user is signed in. It uses
// useWorkspace state to decide between WorkspacePicker and Shell.
//
// useWorkspace lives inside WorkspaceProvider, so this component must
// be rendered as a descendant of WorkspaceProvider — which it is via App.
// ─────────────────────────────────────────────────────────────────────────
function AuthenticatedRouter() {
  // Lazy import to keep the dependency on useWorkspace local — it's the
  // only place the routing decision lives.
  // eslint-disable-next-line global-require
  const { useWorkspace } = require('./hooks/useWorkspace');
  const { availableWorkspaces, currentWorkspaceId, loading } = useWorkspace();
  const { profile } = useAuth();

  if (loading) {
    return (
      <div style={fallbackContainer}>
        <div style={{ fontSize: 14, color: '#6B7280' }}>Loading…</div>
      </div>
    );
  }

  // Signed in but profile hydration failed entirely
  if (!profile) {
    return (
      <div style={fallbackContainer}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#003060' }}>
          Account Setup Pending
        </div>
        <div style={{ fontSize: 14, color: '#6B7280', marginTop: 8, textAlign: 'center', maxWidth: 320 }}>
          Your account is being configured. Contact verticalstack at{' '}
          <a href="mailto:hello@verticalstack.ai" style={{ color: '#00B8D0' }}>
            hello@verticalstack.ai
          </a>
        </div>
        <div style={{ marginTop: 24, fontSize: 12, color: '#9CA3AF' }}>
          verticalstack · stack more, manage less
        </div>
      </div>
    );
  }

  // Signed in but no workspace memberships
  if (!availableWorkspaces || availableWorkspaces.length === 0) {
    return (
      <div style={fallbackContainer}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🏠</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#003060' }}>
          No Workspaces Yet
        </div>
        <div style={{ fontSize: 14, color: '#6B7280', marginTop: 8, textAlign: 'center', maxWidth: 360 }}>
          Your account isn't a member of any workspace yet. Contact verticalstack at{' '}
          <a href="mailto:hello@verticalstack.ai" style={{ color: '#00B8D0' }}>
            hello@verticalstack.ai
          </a>{' '}
          to get added.
        </div>
        <div style={{ marginTop: 24, fontSize: 12, color: '#9CA3AF' }}>
          verticalstack · stack more, manage less
        </div>
      </div>
    );
  }

  // Signed in, 2+ workspaces, none selected → show picker
  if (availableWorkspaces.length > 1 && !currentWorkspaceId) {
    return <WorkspacePicker />;
  }

  // Signed in + workspace ready → render the unified Shell
  return <Shell />;
}

// ── Root route: auth-aware ────────────────────────────────────────────────
// Not signed in → Landing. Signed in → AuthenticatedRouter.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={fallbackContainer}>
        <div style={{ fontSize: 14, color: '#6B7280' }}>Loading…</div>
      </div>
    );
  }
  return user ? <AuthenticatedRouter /> : <Landing />;
}

// ── Protected route wrapper for any path other than '/' ────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={fallbackContainer}>
        <div style={{ fontSize: 14, color: '#6B7280' }}>Loading…</div>
      </div>
    );
  }
  return user ? children : <Navigate to="/login" replace />;
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <BrowserRouter>
          <InstallBanner />
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/guest" element={<GuestPortal />} />
            <Route path="/lease" element={<LeaseSigningPortal />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <AuthenticatedRouter />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </WorkspaceProvider>
    </AuthProvider>
  );
}